import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/layout/footer';
import { supabase } from '../lib/supabase';
import { useLanguage } from '../context/LanguageContext';

interface LandingImage {
  id: string;
  url: string;
  alt_text: string | null;
  alt_text_en: string | null;
  caption: string | null;
  caption_en: string | null;
  link_url: string | null;
  position: number;
  grid_row: number;
}

function LandingImageGrid({ images }: { images: LandingImage[] }) {
  const { localize } = useLanguage();
  const left  = images.find((img) => img.position === 0);
  const right = images.find((img) => img.position === 1);

  return (
    <div className="grid grid-cols-2 h-[60vh] md:h-screen gap-[2px]">
      {[left, right].map((img, i) =>
        img ? (
          <Link
            key={img.id}
            to={img.link_url ?? '/collections'}
            className="group relative overflow-hidden block"
          >
            <img
              src={img.url}
              alt={localize(img, 'alt_text') || ''}
              className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-700 ease-in-out group-hover:scale-105"
            />
            <div className="absolute inset-0 bg-linear-to-t from-black/60 via-transparent to-transparent" />
            {(img.caption || img.caption_en) && (
              <span className="absolute bottom-6 left-6 font-['Sora'] tracking-[0.25em] text-xs md:text-sm text-white uppercase">
                {localize(img, 'caption')}
              </span>
            )}
          </Link>
        ) : (
          <div key={i} className="bg-neutral-900" />
        )
      )}
    </div>
  );
}

export default function HomePage() {
  const { t } = useLanguage();
  const [grid1, setGrid1] = useState<LandingImage[]>([]);
  const [grid2, setGrid2] = useState<LandingImage[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase
      .from('landing_page_images')
      .select('*')
      .eq('is_active', true)
      .order('grid_row')
      .order('position')
      .then(({ data, error }) => {
        if (!error && data) {
          setGrid1(data.filter((img: LandingImage) => img.grid_row === 1));
          setGrid2(data.filter((img: LandingImage) => img.grid_row === 2));
        }
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-neutral-950">
        <div className="w-8 h-8 border-2 border-white/20 border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="bg-neutral-950">
      {/* Grille 1 */}
      <LandingImageGrid images={grid1} />

      {/* Bannière Maison Adji — deux lignes de logos qui scintillent (les logos pulsent en vague, pas de trait) */}
      <div className="relative flex items-center justify-center py-10 md:py-14 px-6">
        {/* Ligne de logos au-dessus : segments qui pulsent en décalé */}
        <div
          className="absolute left-0 right-0 h-8 overflow-hidden pointer-events-none"
          style={{ top: '50%', transform: 'translateY(calc(-50% - 28px))' }}
          aria-hidden
        >
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="logo-twinkle-segment"
              style={{
                backgroundImage: 'url(/assets/images/maisonadjiwhite.png)',
                clipPath: `inset(0 ${87.5 - (i + 1) * 12.5}% 0 ${i * 12.5}%)`,
                animationDelay: `${i * 0.28}s`,
              }}
            />
          ))}
        </div>
        {/* Ligne de logos en-dessous (décalage pour effet décalé) */}
        <div
          className="absolute left-0 right-0 h-8 overflow-hidden pointer-events-none"
          style={{ top: '50%', transform: 'translateY(calc(-50% + 28px))' }}
          aria-hidden
        >
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="logo-twinkle-segment"
              style={{
                backgroundImage: 'url(/assets/images/maisonadjiwhite.png)',
                clipPath: `inset(0 ${87.5 - (i + 1) * 12.5}% 0 ${i * 12.5}%)`,
                animationDelay: `${i * 0.28 + 1.1}s`,
              }}
            />
          ))}
        </div>
        <div className="relative z-10 flex items-center justify-center w-full max-w-4xl">
          <div className="h-px flex-1 bg-white/10 min-w-[60px]" />
          <p className="mx-6 md:mx-10 font-['Sora'] text-xs tracking-[0.35em] text-white/40 uppercase whitespace-nowrap">
            Maison Adji
          </p>
          <div className="h-px flex-1 bg-white/10 min-w-[60px]" />
        </div>
      </div>

      {/* Grille 2 */}
      <LandingImageGrid images={grid2} />

      {/* CTA */}
      <div className="flex flex-col items-center justify-center py-20 md:py-28 px-6 gap-6">
        <p className="font-['Sora'] text-xs tracking-[0.3em] text-white/30 uppercase">
          {t('home.subtitle')}
        </p>
        <h2 className="font-['Sora'] text-2xl md:text-4xl text-white text-center font-light tracking-wide max-w-xl">
          {t('home.tagline')}
        </h2>
        <Link
          to="/collections"
          className="mt-2 inline-flex items-center gap-3 border border-white/30 text-white font-['Rubik'] text-xs tracking-widest uppercase px-8 py-3.5 hover:bg-white hover:text-black transition-all duration-300"
        >
          {t('home.discover')}
        </Link>
      </div>

      <Footer />
    </div>
  );
}
